import { useEffect, useState } from "react";
import axios from "axios";
import { X, Sparkles, Copy, Check, AlertCircle, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIPrayerResponse({ prayer, token, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");

  const generate = async () => {
    setBusy(true);
    setError("");
    setData(null);
    try {
      const r = await axios.post(
        `${API}/admin/ai/prayer-response`,
        { name: prayer.name, request_text: prayer.request },
        { headers: { "X-Admin-Token": token }, timeout: 90000 }
      );
      setData(r.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Claude request failed.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const doCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* noop */ }
  };

  const fullEmail = data
    ? `${data.response}\n\n"${data.scripture_text}"\n— ${data.scripture_ref}\n\nPraying with you,\nAbide Daily Ministry`
    : "";

  const mailto = prayer.email && data
    ? `mailto:${prayer.email}?subject=${encodeURIComponent("Praying for you — Abide Daily Ministry")}&body=${encodeURIComponent(fullEmail)}`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="ai-prayer-response-modal"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}>
              <Sparkles size={18} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Suggested Pastoral Response</div>
              <h2 className="font-display text-xl text-navy-deep mt-0.5">For {prayer.name}</h2>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="ai-prayer-close" className="w-10 h-10 rounded-full hover:bg-cream-100 flex items-center justify-center">
            <X size={20} className="text-navy" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="rounded-xl bg-cream-50 border border-cream-200 p-4">
            <div className="text-xs uppercase tracking-wider text-navy/55 mb-1.5">Original Request</div>
            <p className="text-sm text-navy/85 whitespace-pre-wrap">{prayer.request}</p>
          </div>

          {busy && (
            <div className="flex items-center gap-2 text-navy/70 text-sm" data-testid="ai-prayer-loading">
              <Loader2 size={16} className="animate-spin text-gold" /> Claude is drafting a response…
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 inline-flex items-center gap-2" data-testid="ai-prayer-error">
              <AlertCircle size={14} /> {error}
              <button onClick={generate} className="ml-2 underline text-red-900">Retry</button>
            </div>
          )}

          {data && (
            <>
              <div className="rounded-xl border border-gold/30 bg-gradient-to-br from-cream-50 to-white p-5" data-testid="ai-prayer-scripture">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Suggested Scripture</div>
                  <button
                    onClick={() => doCopy("verse", `"${data.scripture_text}" — ${data.scripture_ref}`)}
                    data-testid="ai-prayer-copy-verse"
                    className="text-xs inline-flex items-center gap-1 text-navy/60 hover:text-navy-deep"
                  >
                    {copied === "verse" ? <Check size={12} /> : <Copy size={12} />} {copied === "verse" ? "Copied" : "Copy"}
                  </button>
                </div>
                <blockquote className="mt-2 font-display text-lg italic text-navy-deep leading-relaxed">
                  “{data.scripture_text}”
                </blockquote>
                <p className="mt-1 text-sm font-semibold text-[var(--gold-600)]">— {data.scripture_ref}</p>
              </div>

              <div className="rounded-xl border border-cream-200 bg-white p-5" data-testid="ai-prayer-reply">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Suggested Reply</div>
                  <button
                    onClick={() => doCopy("reply", data.response)}
                    data-testid="ai-prayer-copy-reply"
                    className="text-xs inline-flex items-center gap-1 text-navy/60 hover:text-navy-deep"
                  >
                    {copied === "reply" ? <Check size={12} /> : <Copy size={12} />} {copied === "reply" ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="text-sm text-navy/90 leading-relaxed whitespace-pre-wrap">{data.response}</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap pt-1">
                <button
                  onClick={() => doCopy("all", fullEmail)}
                  data-testid="ai-prayer-copy-all"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy-deep text-cream text-sm hover:opacity-90"
                >
                  {copied === "all" ? <Check size={14} /> : <Copy size={14} />} Copy full email
                </button>
                {mailto && (
                  <a
                    href={mailto}
                    data-testid="ai-prayer-mailto"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold text-[var(--gold-600)] bg-white text-sm hover:bg-gold/10"
                  >
                    <Mail size={14} /> Open in email
                  </a>
                )}
                <button
                  onClick={generate}
                  disabled={busy}
                  data-testid="ai-prayer-regenerate"
                  className="text-sm text-navy/60 hover:text-navy-deep px-3 py-2 disabled:opacity-50"
                >
                  Regenerate
                </button>
              </div>
              <p className="text-xs text-navy/50 italic">
                Always review and personalize before sending. Claude is an assistant, not a replacement for pastoral care.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
